import { Injectable }                       from '@angular/core';
import { DeviceOrientationEnum }            from '@appConfig/media-config';
import {
	ANALYTICS_CONFIG,
	IAnalyticsDataConfig
}                                           from '@core/analytics/config/analytics-config';
import {
	KnownBrowsersKeysType,
	KnownPlatformsKeysType
}                                           from '@interceptors/model/x-client-analytics-header-model';
import { CurrentUserService }               from '@shared/services/current-user.service';
import { StringUtils }                      from '@utils/helpers';
import { BrowserDetector, KnownBrowsers, KnownPlatforms } from 'browser-dtector';

import { fromEvent }                        from 'rxjs';

@Injectable( { providedIn: 'root' } )
export class AnalyticsDataProviderService {
	// Browser Detector
	private readonly _browser: BrowserDetector = new BrowserDetector(window.navigator.userAgent);
	private _browserInfoFull: {
		name: string;
		platform: string;
		userAgent: string;
		version: string | null;
		shortVersion: string | null;
		isAndroid: boolean;
		isTablet: boolean;
		isMobile: boolean;
		isDesktop: boolean;
		isWebkit: boolean;
		isIE: boolean;
		isChrome: boolean;
		isFireFox: boolean;
		isSafari: boolean;
		isOpera: boolean;
		isEdge: boolean
	};
	private knownPlatforms = KnownPlatforms;
	private knownBrowsers = KnownBrowsers;

	// Config
	readonly analyticsDataConfig: IAnalyticsDataConfig = ANALYTICS_CONFIG;

	// event dependant info
	private _deviceOrientation: DeviceOrientationEnum;
	private _displayDensity: number;
	private _dpi: string;
	private _screenResolution: string;
	private _windowResolution: string;
	private _zoomLevel: number;

	// static info
	private _platform: KnownPlatformsKeysType;
	private _brand: KnownBrowsersKeysType;
	private _version: string;
	private _isTouchDevice: boolean;


	constructor(
		private readonly _currentUserService: CurrentUserService
	) {
		// FF
		if ( !this.analyticsDataConfig.featureEnabled ) { return; }

		this.init();
	}

	init(): void {
		// browser info must be first
		this._browserInfoFull = this._browser.parseUserAgent();

		// before first event occurs
		this.setDynamicValues();
		this.writeDisplayDensity();
		this.writeDeviceOrientation();

		this.subscribeToEventsForDynamicValues();
	}

	private subscribeToEventsForDynamicValues(): void {
		// Resize
		fromEvent( window, 'resize' )
			.subscribe( ( evt ) => {
				this.setDynamicValues();
			} );

		// Display Density
		const displayDensityMedia = matchMedia( `(resolution: ${ window.devicePixelRatio }dppx)` );
		fromEvent( displayDensityMedia, 'change' )
			.subscribe( ( evt ) => {
				this.writeDisplayDensity();
			} );

		// Device Orientation
		const deviceOrientationMedia = matchMedia( `(orientation: portrait)` );
		fromEvent( deviceOrientationMedia, 'change' )
			.subscribe( ( evt ) => {
				this.writeDeviceOrientation();
			} );
	}

	/**
	 * Set dynamic values after change event occurs.
	 */
	private setDynamicValues(): void {
		this.writeScreenResolution();
		this.writeWindowResolution();
		this.writeZoomLevel();
	}

	/**
	 * Determines browser version and name or use cached ones.
	 * Other possibilities: package or own impl like https://stackoverflow.com/questions/71392342/property-useragentdata-does-not-exist-on-type-navigator
	 */
	public getBrowserNameVersion(): string {
		this._brand = this._brand ?? this._browserInfoFull?.name;
		this._version = this._version ?? this._browserInfoFull?.version;
		return `${ this._brand } ${ this._version }`;
	}

	/**
	 * Determines platform or use cached one.
	 */
	public getPlatform(): string {
		// this._platform = this._platform ?? navigator[ this._userAgentData ]?.platform;
		this._platform = this._platform ?? this._browserInfoFull?.platform;
		return `${ this._platform }`;
	}

	/**
	 * Determines touch device or use cached one.
	 */
	public getIsTouchDevice(): boolean {
		this._isTouchDevice = this._isTouchDevice ??
			(
				'ontouchstart' in window ||
				navigator.maxTouchPoints > 0 ||
				navigator[ this.analyticsDataConfig.userAgentData ]?.msMaxTouchPoints > 0
			);

		return this._isTouchDevice;
	}

	public getDeviceOrientation(): DeviceOrientationEnum {
		return this._deviceOrientation;
	}

	private writeDeviceOrientation(): void {
		this._deviceOrientation = matchMedia( `(orientation: portrait)` ).matches
			? DeviceOrientationEnum.PORTRAIT
			: DeviceOrientationEnum.LANDSCAPE;
	}

	/**
	 * Not real display dpi, at least on my Win it gives 96 constantly on both of my different monitors
	 * Probably some rescaled value by OS or browser core... Chrome and the Edge behave the same...
	 */
	public getDisplayDensity(): number {
		return this._displayDensity;
	}

	private writeDisplayDensity(): void {
		this.writeZoomLevel();
		this._displayDensity = Math.floor( window.devicePixelRatio * 100 ) / this.getZoomLevel();
		this._dpi = `${ this._displayDensity * 96 }dpi`;
	}

	public getDpi(): string {
		return this._dpi;
	}

	public getScreenResolution(): string {
		return this._screenResolution;
	}

	private writeScreenResolution(): void {
		this._screenResolution = `${ screen.width } x ${ screen.height }`;
	}

	public getWindowResolution(): string {
		return this._windowResolution;
	}

	private writeWindowResolution(): void {
		this._windowResolution = `${ window.innerWidth } x ${ window.innerHeight }`;
	}

	public getZoomLevel(): number {
		return this._zoomLevel;
	}

	private writeZoomLevel(): void {
		this._zoomLevel = Math.floor( window.devicePixelRatio * 100 );
	}

	public getUserId(): string {
		return this._currentUserService.getCurrentUserId();
	}

	public getUserName(): string {
		return StringUtils.replaceEmptyValue( this._currentUserService.getCurrentUser()?.firstname )
			+ ' '
			+ StringUtils.replaceEmptyValue( this._currentUserService.getCurrentUser()?.lastname );
	}

	public getUserRoles(): string {
		return this._currentUserService.getRoles().toString();
	}

	public hasSupportRole(): boolean {
		return this._currentUserService.hasCurrentUserSupportRole();
	}

}
